import { useCallback, useEffect, useRef, useState, type ComponentType } from "react";
import {
  Accessibility,
  X,
  RotateCcw,
  Type,
  AlignLeft,
  AlignRight,
  AlignJustify,
  Contrast,
  Droplet,
  Link2,
  Heading,
  BookOpen,
  BookA,
  MousePointer2,
  PauseCircle,
  Rows3,
  MoveVertical,
  ImageOff,
  MessageSquare,
  Focus,
  Volume2,
  ListTree,
  Ruler,
  Maximize2,
  Minus,
  Plus,
  Check,
  ArrowLeftRight,
} from "lucide-react";
import {
  DEFAULTS,
  PROFILES,
  STORAGE_KEY,
  applySettings,
  countActive,
  isProfileActive,
  loadSettings,
  saveSettings,
  toggleProfile,
  type Align,
  type Contrast3,
  type Saturation,
  type Settings,
} from "@/lib/a11y-settings";

type Icon = ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

/* ---------- Controls (module scope so they keep focus across re-renders) ---------- */

function Toggle({ label, icon: Icon, on, onClick }: { label: string; icon: Icon; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`relative flex min-h-[4.5rem] flex-col items-center justify-center gap-1.5 rounded-xl border-2 p-2.5 text-center text-xs font-medium leading-tight transition-colors ${FOCUS} ${
        on ? "border-primary bg-primary/10 text-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground"
      }`}
    >
      {on && (
        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
          <Check className="h-3 w-3" aria-hidden />
        </span>
      )}
      <Icon className="h-5 w-5" aria-hidden />
      {label}
    </button>
  );
}

function Stepper({
  label,
  icon: Icon,
  value,
  max,
  display,
  onChange,
}: {
  label: string;
  icon: Icon;
  value: number;
  max: number;
  display: (v: number) => string;
  onChange: (v: number) => void;
}) {
  const atMin = value <= 0;
  const atMax = value >= max;
  const stepBtn = `flex h-9 w-9 items-center justify-center rounded-lg border-2 border-border bg-background text-foreground transition-colors hover:border-primary/50 aria-disabled:opacity-40 aria-disabled:hover:border-border ${FOCUS}`;
  return (
    <div role="group" aria-label={label} className="flex items-center justify-between gap-3 rounded-xl border-2 border-border bg-card p-3">
      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <button type="button" aria-label={`Decrease ${label.toLowerCase()}`} aria-disabled={atMin} className={stepBtn} onClick={() => !atMin && onChange(value - 1)}>
          <Minus className="h-4 w-4" aria-hidden />
        </button>
        <span className="min-w-[3.25rem] text-center text-xs font-semibold tabular-nums text-foreground">{display(value)}</span>
        <button type="button" aria-label={`Increase ${label.toLowerCase()}`} aria-disabled={atMax} className={stepBtn} onClick={() => !atMax && onChange(value + 1)}>
          <Plus className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}

function Segment<T extends string>({
  label,
  icon: Icon,
  value,
  options,
  onChange,
}: {
  label: string;
  icon: Icon;
  value: T;
  options: { v: T; l: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="rounded-xl border-2 border-border bg-card p-3">
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="h-4 w-4" aria-hidden />
        {label}
      </div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
        {options.map((o) => {
          const on = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              aria-pressed={on}
              className={`flex min-h-9 items-center justify-center gap-1 rounded-lg border-2 px-1.5 text-xs transition-colors ${FOCUS} ${
                on
                  ? "border-primary bg-primary font-bold text-primary-foreground"
                  : "border-transparent bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {on && <Check className="h-3 w-3 shrink-0" aria-hidden />}
              {o.l}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return <h3 className="mb-2 mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground first:mt-0">{children}</h3>;
}

/* ---------- Widget ---------- */

type Structure = {
  headings: { text: string; level: number; el: HTMLElement }[];
  links: { text: string; el: HTMLAnchorElement }[];
};

export function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<Settings>(DEFAULTS);
  const [guideY, setGuideY] = useState(-100);
  const [definition, setDefinition] = useState<{ word: string; text: string } | null>(null);
  const [structure, setStructure] = useState<Structure | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const lastSpoken = useRef("");
  const launcherRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const announce = useCallback((msg: string) => {
    // Clear first so repeating the same message is still read out.
    setAnnouncement("");
    window.setTimeout(() => setAnnouncement(msg), 30);
  }, []);

  const commit = useCallback((next: Settings) => {
    applySettings(next);
    saveSettings(next);
    setSettings(next);
  }, []);

  const update = useCallback(
    (patch: Partial<Settings>, msg?: string) => {
      setSettings((prev) => {
        const next = { ...prev, ...patch };
        applySettings(next);
        saveSettings(next);
        return next;
      });
      if (msg) announce(msg);
    },
    [announce],
  );

  const closePanel = useCallback(() => {
    setOpen(false);
    window.requestAnimationFrame(() => launcherRef.current?.focus());
  }, []);

  // Restore saved settings
  useEffect(() => {
    const s = loadSettings();
    setSettings(s);
    applySettings(s);
  }, []);

  // Move focus into the panel when it opens
  useEffect(() => {
    if (open) window.requestAnimationFrame(() => closeRef.current?.focus());
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "u") {
        e.preventDefault();
        setOpen((o) => {
          if (o) window.requestAnimationFrame(() => launcherRef.current?.focus());
          return !o;
        });
      } else if (e.key === "Escape") {
        if (definition) setDefinition(null);
        else if (open) closePanel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, definition, closePanel]);

  // Reading guide follows the pointer and keyboard focus
  useEffect(() => {
    if (!settings.readingGuide) return;
    const onMove = (e: PointerEvent) => setGuideY(e.clientY);
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el?.getBoundingClientRect || el.closest("[data-a11y-widget]")) return;
      const r = el.getBoundingClientRect();
      setGuideY(r.top + r.height / 2);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("focusin", onFocus);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("focusin", onFocus);
    };
  }, [settings.readingGuide]);

  // Pause motion also pauses any playing videos
  useEffect(() => {
    if (!settings.pauseMotion) return;
    const pauseAll = () => document.querySelectorAll("video").forEach((v) => !v.closest("[data-a11y-widget]") && v.pause());
    pauseAll();
    const obs = new MutationObserver(pauseAll);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [settings.pauseMotion]);

  // Read aloud (hover with a short delay, or keyboard focus)
  useEffect(() => {
    if (!settings.readAloud) return;
    if (!("speechSynthesis" in window)) return;
    let timer = 0;
    const SELECTOR = "p,h1,h2,h3,h4,h5,h6,li,a,button,label,td,th,figcaption,blockquote,img[alt]";
    const say = (target: EventTarget | null) => {
      const el = (target as HTMLElement | null)?.closest?.(SELECTOR) as HTMLElement | null;
      if (!el || el.closest("[data-a11y-widget]")) return;
      const text = (
        el.getAttribute("aria-label") ||
        (el.tagName === "IMG" ? el.getAttribute("alt") : el.innerText) ||
        ""
      )
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 400);
      if (!text || text === lastSpoken.current) return;
      lastSpoken.current = text;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "en-US";
      window.speechSynthesis.speak(u);
    };
    const onOver = (e: Event) => {
      window.clearTimeout(timer);
      const t = e.target;
      timer = window.setTimeout(() => say(t), 300);
    };
    const onFocus = (e: Event) => say(e.target);
    document.addEventListener("mouseover", onOver);
    document.addEventListener("focusin", onFocus);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("focusin", onFocus);
      window.speechSynthesis.cancel();
      lastSpoken.current = "";
    };
  }, [settings.readAloud]);

  // Dictionary: double-click (or select) a word
  useEffect(() => {
    if (!settings.dictionary) return;
    const onDbl = async (e: MouseEvent) => {
      if ((e.target as HTMLElement | null)?.closest?.("[data-a11y-widget]")) return;
      const sel = window.getSelection?.()?.toString().trim() || "";
      const word = sel.split(/\s+/)[0]?.replace(/[^a-zA-Z-]/g, "");
      if (!word) return;
      try {
        const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`);
        const data = await res.json();
        const text = data?.[0]?.meanings?.[0]?.definitions?.[0]?.definition;
        setDefinition({ word, text: text || "No definition found for this word." });
      } catch {
        setDefinition({ word, text: "Couldn't load a definition right now." });
      }
    };
    document.addEventListener("dblclick", onDbl);
    return () => document.removeEventListener("dblclick", onDbl);
  }, [settings.dictionary]);

  // Tooltips (JS-driven, one floating node)
  useEffect(() => {
    if (!settings.tooltips) return;
    const tip = document.createElement("div");
    tip.setAttribute("data-a11y-tooltip", "");
    tip.setAttribute("aria-hidden", "true");
    tip.style.cssText =
      "position:fixed;z-index:2147483647;pointer-events:none;background:#111;color:#fff;" +
      "font-size:13px;line-height:1.35;padding:6px 10px;border-radius:6px;max-width:18rem;" +
      "box-shadow:0 6px 20px rgba(0,0,0,.3);opacity:0;transition:opacity .12s ease";
    document.body.appendChild(tip);

    const labelFor = (start: HTMLElement | null): string => {
      let el: HTMLElement | null = start;
      while (el && el !== document.body) {
        if (el.closest("[data-a11y-widget]")) return "";
        const label =
          el.getAttribute("aria-label") || el.getAttribute("title") || (el.tagName === "IMG" ? el.getAttribute("alt") : null);
        if (label) return label;
        el = el.parentElement;
      }
      return "";
    };
    const place = (x: number, y: number) => {
      const r = tip.getBoundingClientRect();
      tip.style.left = `${Math.min(Math.max(8, x + 14), window.innerWidth - r.width - 8)}px`;
      tip.style.top = `${y + 22 + r.height > window.innerHeight ? y - r.height - 12 : y + 22}px`;
    };
    const show = (text: string, x: number, y: number) => {
      if (!text) {
        tip.style.opacity = "0";
        return;
      }
      tip.textContent = text;
      tip.style.opacity = "1";
      place(x, y);
    };
    const onMove = (e: MouseEvent) => show(labelFor(e.target as HTMLElement), e.clientX, e.clientY);
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement;
      const b = el.getBoundingClientRect();
      show(labelFor(el), b.left, b.bottom - 22);
    };
    const hide = () => {
      tip.style.opacity = "0";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("scroll", hide, true);
    window.addEventListener("blur", hide);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("scroll", hide, true);
      window.removeEventListener("blur", hide);
      tip.remove();
    };
  }, [settings.tooltips]);

  const reset = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    setSettings(DEFAULTS);
    applySettings(DEFAULTS);
    setDefinition(null);
    setStructure(null);
    announce("All accessibility settings reset");
  };

  const toggleStructure = () => {
    if (structure) {
      setStructure(null);
      return;
    }
    const scope = document.querySelector("main") ?? document.body;
    const headings = Array.from(scope.querySelectorAll<HTMLElement>("h1, h2, h3, h4"))
      .filter((h) => !h.closest("[data-a11y-widget]") && h.offsetParent !== null)
      .map((h) => ({ text: (h.textContent || "").replace(/\s+/g, " ").trim(), level: Number(h.tagName[1]), el: h }))
      .filter((h) => h.text)
      .slice(0, 60);
    const seen = new Set<string>();
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]"))
      .filter((a) => !a.closest("[data-a11y-widget]") && a.offsetParent !== null)
      .map((a) => ({ text: (a.getAttribute("aria-label") || a.textContent || "").replace(/\s+/g, " ").trim(), el: a }))
      .filter((l) => l.text && !seen.has(l.text) && seen.add(l.text))
      .slice(0, 60);
    setStructure({ headings, links });
  };

  const jumpTo = (el: HTMLElement) => {
    if (!el.hasAttribute("tabindex") && !el.matches("a,button,input,select,textarea")) el.setAttribute("tabindex", "-1");
    el.scrollIntoView({ behavior: settings.pauseMotion ? "auto" : "smooth", block: "center" });
    el.focus({ preventScroll: true });
  };

  const active = countActive(settings);
  const right = settings.position === "right";
  const launcherSide = right ? "right-4" : "left-4";
  const panelSide = right ? "right-0 border-l" : "left-0 border-r";

  const onOff = (label: string, key: keyof Settings) => () => {
    const on = !settings[key];
    update({ [key]: on } as Partial<Settings>, `${label} ${on ? "on" : "off"}`);
  };

  const pct = (v: number) => `${Math.round(100 + v * 12.5)}%`;
  const lvl = (v: number) => (v === 0 ? "Default" : `Level ${v}`);

  return (
    <div data-a11y-widget="">
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      {settings.readingGuide && (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed left-0 z-[2147482000] h-12 w-full border-y-2 border-primary/60 bg-primary/15"
          style={{ top: guideY - 24 }}
        />
      )}

      <button
        ref={launcherRef}
        type="button"
        onClick={() => (open ? closePanel() : setOpen(true))}
        aria-label={active > 0 ? `Accessibility menu, ${active} ${active === 1 ? "setting" : "settings"} on` : "Accessibility menu"}
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-keyshortcuts="Control+U"
        title="Accessibility menu (Ctrl + U)"
        className={`a11y-launcher fixed bottom-24 ${launcherSide} z-[2147483000] flex ${
          settings.oversized ? "h-16 w-16" : "h-12 w-12"
        } items-center justify-center rounded-full border-2 border-primary-foreground/30 bg-primary text-primary-foreground shadow-elegant transition-transform active:scale-95 motion-safe:hover:scale-105 ${FOCUS}`}
      >
        <Accessibility className={settings.oversized ? "h-8 w-8" : "h-6 w-6"} aria-hidden />
        {active > 0 && (
          <span aria-hidden="true" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1 text-[11px] font-bold text-primary">
            {active}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          id="a11y-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="a11y-panel-title"
          className={`fixed inset-y-0 ${panelSide} z-[2147483001] flex w-full flex-col border-border bg-background shadow-elegant sm:w-[24rem] motion-safe:animate-in motion-safe:fade-in`}
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
              <h2 id="a11y-panel-title" className="font-display text-lg font-bold text-foreground">
                Accessibility
              </h2>
              <p className="text-xs text-muted-foreground">Changes apply right away and are saved on this device.</p>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={closePanel}
              aria-label="Close accessibility menu"
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-foreground hover:bg-muted ${FOCUS}`}
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4">
            <SectionLabel>Profiles</SectionLabel>
            <div className="grid grid-cols-2 gap-2">
              {PROFILES.map((p) => {
                const on = isProfileActive(settings, p);
                return (
                  <button
                    key={p.label}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      commit(toggleProfile(settings, p));
                      announce(`${p.label} profile ${on ? "off" : "on"}`);
                    }}
                    className={`relative rounded-xl border-2 px-3 py-2.5 text-left transition-colors ${FOCUS} ${
                      on ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    <span className="flex items-center gap-1.5 pr-4 text-sm font-semibold text-foreground">{p.label}</span>
                    <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{p.description}</span>
                    {on && (
                      <span className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" aria-hidden />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <SectionLabel>Text</SectionLabel>
            <div className="space-y-2">
              <Stepper label="Text size" icon={Type} value={settings.fontScale} max={4} display={pct} onChange={(v) => update({ fontScale: v }, `Text size ${pct(v)}`)} />
              <Stepper label="Letter spacing" icon={Rows3} value={settings.spacing} max={3} display={lvl} onChange={(v) => update({ spacing: v }, `Letter spacing ${lvl(v)}`)} />
              <Stepper label="Line height" icon={MoveVertical} value={settings.lineHeight} max={3} display={lvl} onChange={(v) => update({ lineHeight: v }, `Line height ${lvl(v)}`)} />
              <Segment
                label="Text align"
                icon={AlignJustify}
                value={settings.align}
                options={[
                  { v: "off" as Align, l: "Default" },
                  { v: "left" as Align, l: "Left" },
                  { v: "center" as Align, l: "Center" },
                  { v: "right" as Align, l: "Right" },
                ]}
                onChange={(v) => update({ align: v }, `Text align ${v === "off" ? "default" : v}`)}
              />
              <div className="grid grid-cols-3 gap-2">
                <Toggle label="Dyslexia font" icon={BookOpen} on={settings.dyslexia} onClick={onOff("Dyslexia font", "dyslexia")} />
                <Toggle label="Highlight links" icon={Link2} on={settings.links} onClick={onOff("Highlight links", "links")} />
                <Toggle label="Highlight headings" icon={Heading} on={settings.headings} onClick={onOff("Highlight headings", "headings")} />
              </div>
            </div>

            <SectionLabel>Color</SectionLabel>
            <div className="space-y-2">
              <Segment
                label="Contrast"
                icon={Contrast}
                value={settings.contrast}
                options={[
                  { v: "off" as Contrast3, l: "Default" },
                  { v: "high" as Contrast3, l: "High" },
                  { v: "invert" as Contrast3, l: "Invert" },
                ]}
                onChange={(v) => update({ contrast: v }, `Contrast ${v === "off" ? "default" : v}`)}
              />
              <Segment
                label="Saturation"
                icon={Droplet}
                value={settings.saturation}
                options={[
                  { v: "off" as Saturation, l: "Default" },
                  { v: "grayscale" as Saturation, l: "Gray" },
                  { v: "low" as Saturation, l: "Low" },
                  { v: "high" as Saturation, l: "High" },
                ]}
                onChange={(v) => update({ saturation: v }, `Saturation ${v === "off" ? "default" : v}`)}
              />
            </div>

            <SectionLabel>Navigation & reading</SectionLabel>
            <div className="grid grid-cols-3 gap-2">
              <Toggle label="Reading guide" icon={Ruler} on={settings.readingGuide} onClick={onOff("Reading guide", "readingGuide")} />
              <Toggle label="Read aloud" icon={Volume2} on={settings.readAloud} onClick={onOff("Read aloud", "readAloud")} />
              <Toggle label="Dictionary" icon={BookA} on={settings.dictionary} onClick={onOff("Dictionary", "dictionary")} />
              <Toggle label="Big cursor" icon={MousePointer2} on={settings.bigCursor} onClick={onOff("Big cursor", "bigCursor")} />
              <Toggle label="Focus outline" icon={Focus} on={settings.focusOutline} onClick={onOff("Focus outline", "focusOutline")} />
              <Toggle label="Tooltips" icon={MessageSquare} on={settings.tooltips} onClick={onOff("Tooltips", "tooltips")} />
              <Toggle label="Pause motion" icon={PauseCircle} on={settings.pauseMotion} onClick={onOff("Pause motion", "pauseMotion")} />
              <Toggle label="Hide images" icon={ImageOff} on={settings.hideImages} onClick={onOff("Hide images", "hideImages")} />
              <Toggle label="Bigger button" icon={Maximize2} on={settings.oversized} onClick={onOff("Bigger button", "oversized")} />
            </div>

            <button
              type="button"
              onClick={toggleStructure}
              aria-expanded={!!structure}
              aria-controls="a11y-structure"
              className={`mt-2 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-border bg-card px-3 py-2.5 text-sm font-medium text-foreground hover:border-primary/50 ${FOCUS}`}
            >
              <ListTree className="h-4 w-4" aria-hidden /> {structure ? "Hide page structure" : "Show page structure"}
            </button>

            {structure && (
              <div id="a11y-structure" className="mt-2 rounded-xl border-2 border-border bg-card p-3 text-sm">
                <h3 className="mb-1.5 font-semibold text-foreground">Headings on this page</h3>
                {structure.headings.length === 0 && <p className="text-muted-foreground">No headings found.</p>}
                <ul className="mb-4 space-y-0.5">
                  {structure.headings.map((h, i) => (
                    <li key={i} style={{ paddingLeft: `${(h.level - 1) * 0.75}rem` }}>
                      <button
                        type="button"
                        onClick={() => jumpTo(h.el)}
                        className={`w-full rounded-md px-1.5 py-1 text-left text-muted-foreground hover:bg-muted hover:text-foreground ${FOCUS}`}
                      >
                        <span className="mr-1.5 text-[10px] font-bold text-primary">H{h.level}</span>
                        {h.text}
                      </button>
                    </li>
                  ))}
                </ul>
                <h3 className="mb-1.5 font-semibold text-foreground">Links</h3>
                <ul className="space-y-0.5">
                  {structure.links.map((l, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => jumpTo(l.el)}
                        className={`w-full rounded-md px-1.5 py-1 text-left text-muted-foreground hover:bg-muted hover:text-foreground ${FOCUS}`}
                      >
                        {l.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-border px-4 py-3">
            <button
              type="button"
              onClick={reset}
              className={`flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 ${FOCUS}`}
            >
              <RotateCcw className="h-4 w-4" aria-hidden /> Reset all
            </button>
            <button
              type="button"
              onClick={() => update({ position: right ? "left" : "right" }, `Menu moved to the ${right ? "left" : "right"}`)}
              aria-label={`Move menu to the ${right ? "left" : "right"} side`}
              title={`Move to the ${right ? "left" : "right"} side`}
              className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 border-border bg-card text-foreground hover:border-primary/50 ${FOCUS}`}
            >
              {right ? <AlignLeft className="h-4 w-4" aria-hidden /> : <ArrowLeftRight className="h-4 w-4" aria-hidden />}
            </button>
          </div>
          <div className="px-4 pb-3 text-center text-xs text-muted-foreground">
            <a href="/accessibility" className={`underline underline-offset-4 hover:text-foreground ${FOCUS}`}>
              Accessibility statement
            </a>
            <span aria-hidden="true"> · </span>
            <span>
              Shortcut: <kbd className="font-sans font-semibold">Ctrl + U</kbd>
            </span>
          </div>
        </div>
      )}

      {definition && (
        <div
          role="dialog"
          aria-label={`Definition of ${definition.word}`}
          className={`fixed bottom-6 ${right ? "right-4" : "left-4"} z-[2147483002] w-[min(92vw,20rem)] rounded-xl border-2 border-border bg-background p-3 shadow-elegant`}
        >
          <div className="flex items-start justify-between gap-2">
            <div aria-live="polite">
              <div className="text-sm font-semibold text-foreground">{definition.word}</div>
              <p className="mt-1 text-sm text-muted-foreground">{definition.text}</p>
            </div>
            <button
              type="button"
              onClick={() => setDefinition(null)}
              aria-label="Close definition"
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground ${FOCUS}`}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
